import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { normalizeLoad } from "../domain/load";
import { defaultSearchValues } from "../schemas/search";
import {
  bookLoadRequest,
  deleteSavedLoadRequest,
  fetchCarriers,
  fetchSavedLoads,
  fetchUserDocuments,
  fetchUserProfile,
  saveLoadRequest,
  searchLoads,
  startSSESearch,
} from "../services/loads";
import { API_URL } from "../config";


const buildSearchPayload = ({ values, profile, carrier }) => {
  const activeCarrier =
    profile?.account_type === "dispatcher" ? carrier : profile;
  const truckstopId = activeCarrier?.truckstop_integration_id;
  const chNumber = activeCarrier?.ch_t_number || profile?.ch_t_number;
  const useParade = !values.enableBackhaul;
  const useTruckstop = Boolean(values.enableTruckstop && truckstopId);


  return {
    payload: {
      origin_city: values.origin_city?.trim(),
      origin_state: values.origin_state?.trim(),
      destination_city: values.destination_city?.trim() || undefined,
      destination_state: values.destination_state?.trim() || undefined,
      ship_date: values.ship_date,
      max_weight: values.max_weight,
      max_length: values.max_length,
      truck_types: values.truck_types?.length
        ? values.truck_types
        : defaultSearchValues.truck_types,
      origin_range: values.origin_range,
      destination_range: values.destination_range,
      min_distance: values.min_distance ?? defaultSearchValues.min_distance,
      max_distance: values.max_distance || undefined,
      user_integration_id: truckstopId,
      user_ch_t_number: chNumber,
    },
    params: {
      use_truckstop: useTruckstop,
      use_parade: useParade,
    },
  };
};

export const useUserProfile = () =>
  useQuery({
    queryKey: ["profile"],
    queryFn: fetchUserProfile,
  });

export const useCarriers = (enabled) =>
  useQuery({
    queryKey: ["carriers"],
    queryFn: fetchCarriers,
    enabled,
  });

export const useSavedLoads = (enabled = true) =>
  useQuery({
    queryKey: ["savedLoads"],
    queryFn: async () => {
      const data = await fetchSavedLoads();
      return data.map((load) => normalizeLoad({ ...load, isSaved: true }));
    },
    enabled,
    staleTime: 1000 * 60 * 2,
  });



export const useSearchLoads = () =>
  useMutation({
    mutationFn: async ({ profile, carrier, values }) => {
      const { payload, params, } = buildSearchPayload({
        values,
        profile,
        carrier,
      });

      console.log("[Load Search] Searching loads with payload:", payload)

      const isBackhaul = Boolean(values.enableBackhaul);
      const endpoint = isBackhaul
        ? `${API_URL}/loads/backhaul-search`
        : `${API_URL}/loads/search`;

      const queryParams = isBackhaul
        ? { use_truckstop: params.use_truckstop }
        : { ...params, use_parade: true, use_tql: true };

      const data = await searchLoads({
        endpoint,
        payload,
        params: queryParams,
      });
      if (values.enableBackhaul) {
        const pairs = Array.isArray(data)
          ? data.map((pair) => ({
              ...pair,
              outbound: normalizeLoad(pair.outbound),
              return: normalizeLoad(pair.return),
            }))
          : [];
        return { type: "backhaul", results: pairs };
      }

      const loads = Array.isArray(data?.loads) ? data.loads : [];
      return {
        type: "standard", 
        results: loads.map((load) => normalizeLoad(load)),
      };
    },
  });

/**
 * SSE-based search hook for real-time load updates
 * Returns a mutation that starts an SSE search session
 */
export const useStartSSESearch = () =>
  useMutation({
    mutationFn: async ({ profile, carrier, values }) => {
      const { payload, params } = buildSearchPayload({
        values,
        profile,
        carrier,
      });

      console.log("[SSE Search] Starting SSE search with payload:", payload);

      const isBackhaul = Boolean(values.enableBackhaul);
      
      // For backhaul, use regular search (SSE not supported yet)
      if (isBackhaul) {
        throw new Error("SSE search not supported for backhaul searches");
      }

      const queryParams = {
        ...params,
        use_parade: true,
        use_tql: true,
      };

      const data = await startSSESearch({
        payload,
        params: queryParams,
      });

      return {
        search_id: data.search_id,
        max_lifetime_seconds: data.max_lifetime_seconds,
        heartbeat_interval_seconds: data.heartbeat_interval_seconds,
      };
    },
  });

export const useSaveLoad = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveLoadRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savedLoads"] });
    },
  });
};

export const useDeleteSavedLoad = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSavedLoadRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savedLoads"] });
    },
  });
};

export const useBookLoad = () =>
  useMutation({
    mutationFn: bookLoadRequest,
  });

export const useUserDocuments = () =>
  useQuery({
    queryKey: ["userDocuments"],
    queryFn: fetchUserDocuments,
  });

